import { fetchDashboardDeferred, fetchPartnerDashboardJobStatus, fetchPlatformDashboardJobStatus } from "../api/client";
import { parseDashboardJobPollBody } from "../hooks/dashboardDeferredPoll";

/** Whether a completed job belongs to this dashboard query. */
export function dashboardJobRouteMatches(jobRoute, scope, subpath, params = {}) {
  if (!jobRoute) return false;
  if (jobRoute === "atendimento:relacionamento:summary") {
    return (
      scope === "atendimento" &&
      subpath === "summary" &&
      String(params.tipo_scope || "").trim().toLowerCase() === "relacionamento"
    );
  }
  if (jobRoute === "atendimento:summary" || jobRoute === "partner:atendimento:summary") {
    const scoped = String(params.tipo_scope || "").trim().toLowerCase();
    if (scoped === "relacionamento") return false;
    return scope === "atendimento" && subpath === "summary";
  }
  if (jobRoute.startsWith("clientes:charts:") || jobRoute === "partner:clientes:charts") {
    const type =
      jobRoute.startsWith("partner:")
        ? String(params.type || "all").toLowerCase()
        : jobRoute.split(":")[2] || "summary";
    return (
      scope === "clientes" &&
      subpath === "charts" &&
      String(params.type || "summary").toLowerCase() === type
    );
  }
  if (jobRoute.startsWith("platform:")) {
    return jobRoute === `platform:${scope}:${subpath}`;
  }
  if (jobRoute.startsWith("partner:")) {
    return jobRoute === `partner:${scope}:${subpath}`;
  }
  return false;
}

export function surveyJobRouteMatches(jobRoute, surveyId) {
  if (!jobRoute || !surveyId) return false;
  const prefix = "surveys:analytics:";
  if (!jobRoute.startsWith(prefix)) return false;
  const sid = jobRoute.slice(prefix.length);
  return String(surveyId) === String(sid);
}

/**
 * Loads a finished (or in-flight) dashboard job when opening from a notification.
 * @returns {Promise<{ kind: 'ready', payload }|{ kind: 'pending', jobId, pollAfterMs }|{ kind: 'skip' }>}
 */
export async function bootstrapDashboardJob(priorityJobId, { scope, subpath, params } = {}) {
  const jobId = String(priorityJobId || "").trim();
  if (!jobId) return { kind: "skip" };

  try {
    const jobRes = await fetchDashboardDeferred(`/dashboard/jobs/${encodeURIComponent(jobId)}`);
    const parsed = parseDashboardJobPollBody(jobRes);
    const jobRoute = jobRes?.data?.route || jobRes?.route || null;

    if (parsed.kind === "ready") {
      if (dashboardJobRouteMatches(jobRoute, scope, subpath, params)) {
        return { kind: "ready", payload: parsed.payload };
      }
      return { kind: "skip" };
    }

    if (parsed.kind === "pending") {
      if (dashboardJobRouteMatches(jobRoute, scope, subpath, params)) {
        return {
          kind: "pending",
          jobId,
          pollAfterMs: Math.max(500, Number(jobRes?.data?.pollAfterMs) || 1500),
        };
      }
      return { kind: "skip" };
    }

    if (parsed.kind === "failed") {
      return { kind: "skip" };
    }
  } catch {
    return { kind: "skip" };
  }

  return { kind: "skip" };
}

export async function bootstrapSurveyJob(priorityJobId, surveyId) {
  const jobId = String(priorityJobId || "").trim();
  if (!jobId || !surveyId) return { kind: "skip" };

  try {
    const jobRes = await fetchDashboardDeferred(`/dashboard/jobs/${encodeURIComponent(jobId)}`);
    const parsed = parseDashboardJobPollBody(jobRes);
    const jobRoute = jobRes?.data?.route || jobRes?.route || null;

    if (!surveyJobRouteMatches(jobRoute, surveyId)) {
      return { kind: "skip" };
    }

    if (parsed.kind === "ready") {
      return { kind: "ready", payload: parsed.payload };
    }

    if (parsed.kind === "pending") {
      return {
        kind: "pending",
        jobId,
        pollAfterMs: Math.max(500, Number(jobRes?.data?.pollAfterMs) || 1500),
      };
    }
  } catch {
    return { kind: "skip" };
  }

  return { kind: "skip" };
}

export function partnerSurveyJobRouteMatches(jobRoute, partnerSurveyKey) {
  if (!jobRoute || !partnerSurveyKey) return false;
  if (jobRoute.startsWith("platform:surveys:")) {
    const key = jobRoute.slice("platform:surveys:".length);
    return String(partnerSurveyKey) === String(key);
  }
  const prefix = "partner:surveys:";
  if (!jobRoute.startsWith(prefix)) return false;
  const key = jobRoute.slice(prefix.length);
  return String(partnerSurveyKey) === String(key);
}

export async function bootstrapPartnerSurveyJob(priorityJobId, partnerSurveyKey, { variant = "partner" } = {}) {
  const jobId = String(priorityJobId || "").trim();
  const surveyKey = String(partnerSurveyKey || "").trim();
  if (!jobId || !surveyKey) return { kind: "skip" };

  const fetchStatus = variant === "platform" ? fetchPlatformDashboardJobStatus : fetchPartnerDashboardJobStatus;

  try {
    const jobRes = await fetchStatus(jobId);
    const parsed = parseDashboardJobPollBody(jobRes);
    const jobRoute = jobRes?.data?.route || jobRes?.route || null;

    if (!partnerSurveyJobRouteMatches(jobRoute, surveyKey)) {
      return { kind: "skip" };
    }

    if (parsed.kind === "ready") {
      return { kind: "ready", payload: parsed.payload };
    }

    if (parsed.kind === "pending") {
      return {
        kind: "pending",
        jobId,
        pollAfterMs: Math.max(500, Number(jobRes?.data?.pollAfterMs) || 1500),
      };
    }
  } catch {
    return { kind: "skip" };
  }

  return { kind: "skip" };
}

export async function bootstrapPartnerDashboardJob(priorityJobId, { scope, subpath, params, variant = "partner" } = {}) {
  const jobId = String(priorityJobId || "").trim();
  if (!jobId) return { kind: "skip" };

  const fetchStatus = variant === "platform" ? fetchPlatformDashboardJobStatus : fetchPartnerDashboardJobStatus;

  try {
    const jobRes = await fetchStatus(jobId);
    const parsed = parseDashboardJobPollBody(jobRes);
    const jobRoute = jobRes?.data?.route || jobRes?.route || null;

    if (parsed.kind === "ready") {
      if (dashboardJobRouteMatches(jobRoute, scope, subpath, params)) {
        return { kind: "ready", payload: parsed.payload };
      }
      return { kind: "skip" };
    }

    if (parsed.kind === "pending") {
      if (dashboardJobRouteMatches(jobRoute, scope, subpath, params)) {
        return {
          kind: "pending",
          jobId,
          pollAfterMs: Math.max(500, Number(jobRes?.data?.pollAfterMs) || 1500),
        };
      }
      return { kind: "skip" };
    }

    if (parsed.kind === "failed") {
      return { kind: "skip" };
    }
  } catch {
    return { kind: "skip" };
  }

  return { kind: "skip" };
}
